import React, { useState, useEffect } from 'react';
import { db, auth } from '../../firebase';
import { collection, addDoc, getDocs, doc, serverTimestamp, query, where, orderBy, limit, getDoc, updateDoc } from 'firebase/firestore';
import { FiAward, FiUsers, FiClock, FiEdit2, FiEye } from 'react-icons/fi';

function QuizBattleManagement() {
  // Quiz Battle States (moved from QuizManagement.js)
  const [battleForm, setBattleForm] = useState({
    title: '',
    description: '',
    subject: '',
    difficulty: 'intermediate',
    questionCount: 10,
    timeLimit: 15,
    endDate: ''
  });
  const [activeBattles, setActiveBattles] = useState([]);
  const [pastBattles, setPastBattles] = useState([]);
  const [battleLoading, setBattleLoading] = useState(false);
  const [battleError, setBattleError] = useState(null);

  // State for viewing/editing battles
  const [selectedBattle, setSelectedBattle] = useState(null);
  const [showBattleDetailsModal, setShowBattleDetailsModal] = useState(false);

  // State for question filtering options (subjects and topics)
  const [availableSubjects, setAvailableSubjects] = useState([]);
  const [availableTopics, setAvailableTopics] = useState(new Map());

  // State for all questions and selected questions for new battle
  const [allQuestions, setAllQuestions] = useState([]);
  const [selectedQuestionsForNewBattle, setSelectedQuestionsForNewBattle] = useState([]);

  // State for question selection modal
  const [showQuestionSelectionModal, setShowQuestionSelectionModal] = useState(false);

  // State for filtering in modal
  const [filterSearchTerm, setFilterSearchTerm] = useState('');
  const [filterSubject, setFilterSubject] = useState('all');
  const [filterTopic, setFilterTopic] = useState('all'); // Will change to array for multi-select later
  const [filterDifficulty, setFilterDifficulty] = useState('all'); // Will change to array for multi-select later

  // State for editing
  const [isEditing, setIsEditing] = useState(false);
  const [editFormData, setEditFormData] = useState(null);
  const [editError, setEditError] = useState(null);
  const [editLoading, setEditLoading] = useState(false);

  // Add new state for leaderboard
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [selectedBattleLeaderboard, setSelectedBattleLeaderboard] = useState(null);
  const [leaderboardData, setLeaderboardData] = useState([]);

  // New state for participant counts
  const [participantCounts, setParticipantCounts] = useState({});

  // Add new state for leaderboard pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10); // Number of items to show per page

  // Add new state for jump to page input
  const [jumpToPage, setJumpToPage] = useState('');

  useEffect(() => {
    fetchBattles();
    fetchSubjectsAndTopics(); // Fetch subjects and topics for filters/dropdowns
    fetchAllQuestions(); // Fetch all questions for manual selection
  }, []);

  const fetchSubjectsAndTopics = async () => {
    try {
      const questionsRef = collection(db, 'questions');
      const querySnapshot = await getDocs(questionsRef);
      
      const subjectTopicsMap = new Map();
      querySnapshot.docs.forEach(doc => {
        const data = doc.data();
        if (!subjectTopicsMap.has(data.subject)) {
          subjectTopicsMap.set(data.subject, new Set());
        }
        subjectTopicsMap.get(data.subject).add(data.topic);
      });

      setAvailableSubjects(Array.from(subjectTopicsMap.keys()).sort());
      // Convert Sets to Arrays for easier mapping in options
      const topicsArrayMap = new Map();
      subjectTopicsMap.forEach((topicsSet, subject) => {
        topicsArrayMap.set(subject, Array.from(topicsSet).sort());
      });
      setAvailableTopics(topicsArrayMap);

    } catch (error) {
      console.error("Error fetching subjects and topics:", error);
      // Handle error appropriately, maybe set an error state
    }
  };

  const fetchAllQuestions = async () => {
    try {
      const questionsRef = collection(db, 'questions');
      // Order by subject and then topic for better browsing
      const q = query(questionsRef, orderBy('subject'), orderBy('topic'));
      const querySnapshot = await getDocs(q);
      const questionsList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setAllQuestions(questionsList);
    } catch (error) {
      console.error("Error fetching all questions:", error);
      // Handle error appropriately
    }
  };

  const fetchBattles = async () => {
    try {
      setBattleLoading(true);
      const now = new Date();

      // Fetch active battles
      const activeQuery = query(
        collection(db, 'quiz_battles'),
        where('endDate', '>', now),
        orderBy('endDate', 'asc')
      );
      const activeSnapshot = await getDocs(activeQuery);
      const activeBattlesList = activeSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setActiveBattles(activeBattlesList);

      // Fetch past battles
      const pastQuery = query(
        collection(db, 'quiz_battles'),
        where('endDate', '<=', now),
        orderBy('endDate', 'desc'),
        limit(5) // Limiting past battles for brevity, can be adjusted
      );
      const pastSnapshot = await getDocs(pastQuery);
      const pastBattlesList = pastSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setPastBattles(pastBattlesList);

      // Fetch participant counts for all fetched battles
      const allBattles = [...activeBattlesList, ...pastBattlesList];
      const counts = {};
      for (const battle of allBattles) {
        counts[battle.id] = await fetchParticipantCount(battle.id);
      }
      setParticipantCounts(counts);

    } catch (error) {
      console.error('Error fetching battles:', error);
      setBattleError('Failed to load quiz battles');
    } finally {
      setBattleLoading(false);
    }
  };

  const viewBattleDetails = async (battleId) => {
    try {
      const battleRef = doc(db, 'quiz_battles', battleId);
      const battleDoc = await getDoc(battleRef);
      if (battleDoc.exists()) {
        const battleData = { id: battleDoc.id, ...battleDoc.data() };
        setSelectedBattle(battleData);
        // Format endDate for datetime-local input
        const formattedEndDate = battleData.endDate?.toDate().toISOString().slice(0, 16) || '';
        setEditFormData({ ...battleData, endDate: formattedEndDate });
        setIsEditing(false); // Start in view mode
        setShowBattleDetailsModal(true);
      } else {
        console.error('Battle not found:', battleId);
        // Handle case where battle might have been deleted
      }
    } catch (error) {
      console.error('Error fetching battle details:', error);
      // Handle error appropriately
    }
  };

  const handleEditButtonClick = (battle) => {
    // Format endDate for datetime-local input
    const formattedEndDate = battle.endDate?.toDate().toISOString().slice(0, 16) || '';
    setSelectedBattle(battle);
    setEditFormData({ ...battle, endDate: formattedEndDate });
    setIsEditing(true);
    setShowBattleDetailsModal(true);
  };

  const handleEditFormChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const updateBattle = async () => {
    if (!editFormData || !selectedBattle) return;

    setEditLoading(true);
    setEditError(null);

    // Basic Validation
    if (!editFormData.title || !editFormData.subject || !editFormData.endDate) {
      setEditError('Please fill in all required fields');
      setEditLoading(false);
      return;
    }

    try {
      const battleRef = doc(db, 'quiz_battles', selectedBattle.id);
      
      const updatedData = {
         ...editFormData,
         endDate: new Date(editFormData.endDate), // Convert back to Date object
         updatedAt: serverTimestamp(),
         updatedBy: auth.currentUser.uid,
      };

      await updateDoc(battleRef, updatedData);
      
      // Refresh battles list and close modal
      await fetchBattles();
      setShowBattleDetailsModal(false);
      setSelectedBattle(null);
      setEditFormData(null);
      setIsEditing(false);

    } catch (error) {
      console.error('Error updating battle:', error);
      setEditError('Failed to update battle');
    } finally {
      setEditLoading(false);
    }
  };

  const createBattle = async () => {
    try {
      setBattleLoading(true);
      setBattleError(null);

      // Validate form
      if (!battleForm.title || !battleForm.subject || !battleForm.endDate) {
        setBattleError('Please fill in all required fields');
        return;
      }

      // Create new battle
      const battleData = {
        ...battleForm,
        endDate: new Date(battleForm.endDate),
        createdAt: serverTimestamp(),
        createdBy: auth.currentUser.uid,
        status: 'active',
        // Use selected questions instead of filtering based on form fields
        questions: selectedQuestionsForNewBattle.map(q => ({ id: q.id, ...q })), // Store selected question data
      };

      await addDoc(collection(db, 'quiz_battles'), battleData);

      // Reset form and selected questions
      setBattleForm({
        title: '',
        description: '',
        subject: '',
        difficulty: 'intermediate',
        questionCount: 10,
        timeLimit: 15,
        endDate: ''
      });
      setSelectedQuestionsForNewBattle([]); // Clear selected questions

      // Refresh battles list
      await fetchBattles();

    } catch (error) {
      console.error('Error creating battle:', error);
      setBattleError('Failed to create battle');
    } finally {
      setBattleLoading(false);
    }
  };

  const handleBattleFormChange = (e) => {
    const { name, value } = e.target;
    setBattleForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Functions for question selection modal
  const openQuestionSelectionModal = () => {
    setShowQuestionSelectionModal(true);
  };

  const closeQuestionSelectionModal = () => {
    setShowQuestionSelectionModal(false);
  };

  // Function to handle question selection/deselection (will be refined later)
  const handleQuestionSelect = (questionId) => {
    setSelectedQuestionsForNewBattle(prevSelected => {
      if (prevSelected.find(q => q.id === questionId)) {
        // Deselect question
        return prevSelected.filter(q => q.id !== questionId);
      } else {
        // Select question (find the full question object from allQuestions)
        const questionToAdd = allQuestions.find(q => q.id === questionId);
        return questionToAdd ? [...prevSelected, questionToAdd] : prevSelected;
      }
    });
  };

  // Function to filter questions based on state filters
  const getFilteredQuestions = () => {
    let filtered = [...allQuestions];

    // Filter by search term
    if (filterSearchTerm) {
      filtered = filtered.filter(q =>
        q.question.toLowerCase().includes(filterSearchTerm.toLowerCase())
      );
    }

    // Filter by subject
    if (filterSubject !== 'all') {
      filtered = filtered.filter(q => q.subject === filterSubject);
    }

    // Filter by topic (single select for now)
    if (filterTopic !== 'all') {
      filtered = filtered.filter(q => q.topic === filterTopic);
    }

     // Filter by difficulty (single select for now)
     if (filterDifficulty !== 'all') {
      filtered = filtered.filter(q => q.difficulty.toLowerCase() === filterDifficulty.toLowerCase());
    }

    return filtered;
  };

  // Add new function to fetch leaderboard data
  const fetchLeaderboardData = async (battleId) => {
    if (!battleId) return; // Ensure battleId is provided
    try {
      // Query the public-quiz-participants collection for this battle
      const participantsRef = collection(db, 'public-quiz-participants');
      const participantsQuery = query(
        participantsRef,
        where('battleId', '==', battleId), // Filter by battleId
        where('completed', '==', true),
        orderBy('score', 'desc'),
        orderBy('completedAt', 'asc') // Secondary sort by completion time
      );
      const participantsSnapshot = await getDocs(participantsQuery);

      // Process documents to get unique users with best scores (if multiple attempts)
      const uniqueUserScores = new Map();
      participantsSnapshot.docs.forEach(doc => {
        const data = doc.data();
        if (data.userId) {
          const existingEntry = uniqueUserScores.get(data.userId);

          if (!existingEntry ||
              (data.score > existingEntry.score ||
               (data.score === existingEntry.score &&
                (data.completedAt?.toMillis() - data.startedAt?.toMillis()) <
                (existingEntry.completedAt?.toMillis() - existingEntry.startedAt?.toMillis())))) {
            uniqueUserScores.set(data.userId, {
              id: doc.id, // Participant document ID
              userId: data.userId,
              userName: data.userName || 'Anonymous', // Use stored userName
              score: data.score,
              timeTaken: typeof data.timeTaken === 'number' ? data.timeTaken : 0, // Ensure timeTaken is a number
              completedAt: data.completedAt
            });
          }
        }
      });

      // Convert map to array and sort (should largely follow query)
      const sortedLeaderboard = Array.from(uniqueUserScores.values()).sort((a, b) => {
        if (b.score !== a.score) return b.score - a.score; // Sort by score descending
        return (a.timeTaken || Infinity) - (b.timeTaken || Infinity); // Then by time ascending
      });

      // Add rank for display
      const leaderboardWithRank = sortedLeaderboard.map((entry, index) => ({
          ...entry,
          rank: index + 1
      }));

      setLeaderboardData(leaderboardWithRank);

    } catch (error) {
      console.error('Error fetching admin leaderboard:', error);
      // Handle error appropriately in the UI if needed
    }
  };

  // Add function to view leaderboard
  const viewLeaderboard = async (battle) => {
    setSelectedBattleLeaderboard(battle);
    // Fetch leaderboard data when modal is opened
    await fetchLeaderboardData(battle.id);
    setShowLeaderboard(true);
  };

  // Function to fetch participant count for a given battle
  const fetchParticipantCount = async (battleId) => {
    try {
      const participantsRef = collection(db, 'public-quiz-participants');
      const q = query(participantsRef, where('battleId', '==', battleId), where('completed', '==', true));
      const snapshot = await getDocs(q);
      return snapshot.size;
    } catch (error) {
      console.error(`Error fetching participant count for battle ${battleId}:`, error);
      return 0;
    }
  };

  // Function to format time in seconds to "Xm Ys" format
  const formatTime = (seconds) => {
    if (typeof seconds !== 'number' || isNaN(seconds) || seconds < 0) {
        return '--m --s'; // Handle invalid time values gracefully
    }
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}m ${remainingSeconds}s`;
  };

  // Add new function to handle pagination
  const getPaginatedLeaderboardData = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return leaderboardData.slice(startIndex, endIndex);
  };

  // Add function to handle page change
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Add function to calculate total pages
  const getTotalPages = () => {
    return Math.ceil(leaderboardData.length / itemsPerPage);
  };

  // Add function to handle jump to page
  const handleJumpToPage = (e) => {
    e.preventDefault();
    const pageNumber = parseInt(jumpToPage);
    if (pageNumber && pageNumber > 0 && pageNumber <= getTotalPages()) {
      handlePageChange(pageNumber);
      setJumpToPage('');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-[rgb(130,88,18)]">Quiz Battle Administration</h2>
        <div className="flex items-center space-x-2">
          <FiUsers className="w-6 h-6 text-[rgb(130,88,18)]" />
          <span className="text-gray-600">Manage and monitor quiz battles</span>
        </div>
      </div>
      
      {battleError && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-red-600">{battleError}</p>
        </div>
      )}

      {/* Create New Battle Section */}
      <div className="bg-white rounded-lg shadow-lg p-6 border border-gray-100">
        <div className="flex items-center space-x-2 mb-4">
          <FiAward className="w-5 h-5 text-[rgb(130,88,18)]" />
          <h3 className="text-xl font-semibold text-[rgb(130,88,18)]">Create New Battle</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700">Title</label>
            <input
              type="text"
              name="title"
              value={battleForm.title}
              onChange={handleBattleFormChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[rgb(130,88,18)] focus:ring-[rgb(130,88,18)]"
              placeholder="Enter battle title"
              disabled={battleLoading}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Description</label>
            <input
              type="text"
              name="description"
              value={battleForm.description}
              onChange={handleBattleFormChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-iof focus:ring-iof"
              placeholder="Enter battle description"
              disabled={battleLoading}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Subject</label>
            <select
              name="subject"
              value={battleForm.subject}
              onChange={handleBattleFormChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-iof focus:ring-iof"
              disabled={battleLoading}
            >
              <option value="">Select Subject</option>
              {availableSubjects.map(subject => (
                <option key={subject} value={subject}>{subject}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Difficulty</label>
            <select
              name="difficulty"
              value={battleForm.difficulty}
              onChange={handleBattleFormChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-iof focus:ring-iof"
              disabled={battleLoading}
            >
              <option value="beginner">Beginner</option>
              <option value="intermediate">Intermediate</option>
              <option value="advanced">Advanced</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Time Limit (minutes)</label>
            <input
              type="number"
              name="timeLimit"
              min="5"
              max="180"
              value={battleForm.timeLimit}
              onChange={handleBattleFormChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-iof focus:ring-iof"
              placeholder="Time limit in minutes"
              disabled={battleLoading}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">End Date</label>
            <input
              type="datetime-local"
              name="endDate"
              value={battleForm.endDate}
              onChange={handleBattleFormChange}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-iof focus:ring-iof"
              disabled={battleLoading}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Select Questions</label>
            <button
              onClick={openQuestionSelectionModal}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              disabled={battleLoading}
            >
              Select Questions ({selectedQuestionsForNewBattle.length})
            </button>
          </div>
        </div>

        <button
          onClick={createBattle}
          className="mt-6 w-full bg-[rgb(130,88,18)] text-white py-2 px-4 rounded-md hover:bg-[rgb(110,68,0)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
          disabled={battleLoading || selectedQuestionsForNewBattle.length === 0}
        >
          <FiAward className="w-5 h-5" />
          <span>{battleLoading ? 'Creating...' : 'Create Battle'}</span>
        </button>
      </div>

      {/* Active Battles Section */}
      <div className="bg-white rounded-lg shadow-lg p-6 border border-gray-100">
        <div className="flex items-center space-x-2 mb-4">
          <FiClock className="w-5 h-5 text-[rgb(130,88,18)]" />
          <h3 className="text-xl font-semibold text-[rgb(130,88,18)]">Active Battles</h3>
        </div>

        {battleLoading ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[rgb(130,88,18)]"></div>
          </div>
        ) : (
          <div className="space-y-4">
            {activeBattles.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No active battles at the moment.</p>
            ) : (
              activeBattles.map(battle => (
                <div key={battle.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex justify-between items-start">
                    <div className="space-y-2">
                      <h4 className="font-semibold text-lg text-[rgb(130,88,18)]">{battle.title}</h4>
                      <p className="text-sm text-gray-600">{battle.description}</p>
                      <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                        <span>Subject: {battle.subject}</span>
                        <span>Difficulty: {battle.difficulty}</span>
                        <span>Questions: {battle.questions?.length || 0}</span>
                        <span>Time: {battle.timeLimit} mins</span>
                      </div>
                      <p className="text-sm">
                        <span className="font-medium">Ends:</span> {' '}
                        {battle.endDate?.toDate().toLocaleString()}
                      </p>
                      {/* Display participant count */}
                      <p className="text-sm flex items-center">
                        <FiUsers className="mr-1 text-gray-600" />
                        <span className="font-medium">Participants:</span> {participantCounts[battle.id] !== undefined ? participantCounts[battle.id] : 'Loading...'}
                      </p>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => viewLeaderboard(battle)}
                        className="p-2 text-[rgb(130,88,18)] hover:bg-[rgb(130,88,18)] hover:text-white rounded-md transition-colors"
                        title="View Leaderboard"
                      >
                        <FiAward className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => viewBattleDetails(battle.id)}
                        className="p-2 text-blue-600 hover:bg-blue-600 hover:text-white rounded-md transition-colors"
                        title="View Details"
                      >
                        <FiEye className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleEditButtonClick(battle)}
                        className="p-2 text-yellow-600 hover:bg-yellow-600 hover:text-white rounded-md transition-colors"
                        title="Edit Battle"
                      >
                        <FiEdit2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Past Battles Section */}
      <div className="bg-white rounded-lg shadow-lg p-6 border border-gray-100">
        <div className="flex items-center space-x-2 mb-4">
          <FiClock className="w-5 h-5 text-[rgb(130,88,18)]" />
          <h3 className="text-xl font-semibold text-[rgb(130,88,18)]">Past Battles</h3>
        </div>

        {battleLoading ? (
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[rgb(130,88,18)]"></div>
          </div>
        ) : (
          <div className="space-y-4">
            {pastBattles.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No past battles available.</p>
            ) : (
              pastBattles.map(battle => (
                <div key={battle.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                  <div className="flex justify-between items-start">
                    <div className="space-y-2">
                      <h4 className="font-semibold text-lg text-[rgb(130,88,18)]">{battle.title}</h4>
                      <p className="text-sm text-gray-600">{battle.description}</p>
                      <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                        <span>Subject: {battle.subject}</span>
                        <span>Difficulty: {battle.difficulty}</span>
                        <span>Questions: {battle.questions?.length || 0}</span>
                        <span>Time: {battle.timeLimit} mins</span>
                      </div>
                      <p className="text-sm">
                        Ended: {battle.endDate?.toDate().toLocaleString()}
                      </p>
                      {/* Display participant count */}
                      <p className="text-sm flex items-center">
                        <FiUsers className="mr-1 text-gray-600" />
                        <span className="font-medium">Participants:</span> {participantCounts[battle.id] !== undefined ? participantCounts[battle.id] : 'Loading...'}
                      </p>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => viewLeaderboard(battle)}
                        className="p-2 text-[rgb(130,88,18)] hover:bg-[rgb(130,88,18)] hover:text-white rounded-md transition-colors"
                        title="View Leaderboard"
                      >
                        <FiAward className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => viewBattleDetails(battle.id)}
                        className="p-2 text-blue-600 hover:bg-blue-600 hover:text-white rounded-md transition-colors"
                        title="View Details"
                      >
                        <FiEye className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Leaderboard Modal */}
      {showLeaderboard && selectedBattleLeaderboard && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-2">
                <FiAward className="w-6 h-6 text-[rgb(130,88,18)]" />
                <h3 className="text-xl font-semibold text-[rgb(130,88,18)]">
                  Leaderboard - {selectedBattleLeaderboard.title}
                </h3>
              </div>
              <button
                onClick={() => {
                  setShowLeaderboard(false);
                  setCurrentPage(1); // Reset pagination when closing
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {leaderboardData.length === 0 ? (
              <p className="text-center text-gray-500 py-4">No participants yet.</p>
            ) : (
              <>
                <div className="flex-grow overflow-y-auto space-y-4 pr-2">
                  {getPaginatedLeaderboardData().map((participant, index) => (
                    <div
                      key={participant.id}
                      className={`flex items-center justify-between p-4 rounded-lg ${
                        index === 0 && currentPage === 1 ? 'bg-yellow-50 border-2 border-yellow-200' :
                        index === 1 && currentPage === 1 ? 'bg-gray-50 border-2 border-gray-200' :
                        index === 2 && currentPage === 1 ? 'bg-orange-50 border-2 border-orange-200' :
                        'bg-white border border-gray-200'
                      }`}
                    >
                      <div className="flex items-center space-x-4">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          index === 0 && currentPage === 1 ? 'bg-yellow-400' :
                          index === 1 && currentPage === 1 ? 'bg-gray-400' :
                          index === 2 && currentPage === 1 ? 'bg-orange-400' :
                          'bg-gray-200'
                        }`}>
                          <span className="text-white font-bold">
                            {((currentPage - 1) * itemsPerPage) + index + 1}
                          </span>
                        </div>
                        <div>
                          <p className="font-semibold">{participant.userName || 'Anonymous'}</p>
                          <p className="text-sm text-gray-600">
                            Completed: {participant.completedAt?.toDate().toLocaleString()}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-[rgb(130,88,18)]">
                          {participant.score}%
                        </p>
                        {participant.timeTaken !== undefined && (
                          <p className="text-sm text-gray-600">
                            Time: {formatTime(participant.timeTaken)}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Pagination Controls */}
                <div className="mt-4 pt-4 border-t flex justify-between items-center">
                  <div className="text-sm text-gray-600">
                    Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, leaderboardData.length)} of {leaderboardData.length} entries
                  </div>
                  <div className="flex items-center space-x-4">
                    <button
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="px-3 py-1 rounded-md bg-gray-200 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    
                    <form onSubmit={handleJumpToPage} className="flex items-center space-x-2">
                      <span className="text-sm text-gray-600">Page</span>
                      <input
                        type="number"
                        min="1"
                        max={getTotalPages()}
                        value={jumpToPage}
                        onChange={(e) => setJumpToPage(e.target.value)}
                        className="w-16 px-2 py-1 rounded-md border border-gray-300 focus:border-[rgb(130,88,18)] focus:ring-[rgb(130,88,18)] text-sm"
                        placeholder={currentPage}
                      />
                      <span className="text-sm text-gray-600">of {getTotalPages()}</span>
                      <button
                        type="submit"
                        className="px-3 py-1 rounded-md bg-[rgb(130,88,18)] text-white hover:bg-[rgb(110,68,0)]"
                      >
                        Go
                      </button>
                    </form>

                    <button
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === getTotalPages()}
                      className="px-3 py-1 rounded-md bg-gray-200 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Battle Details Modal */}
      {showBattleDetailsModal && selectedBattle && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full space-y-4">
            <h3 className="text-xl font-semibold mb-4">{isEditing ? 'Edit Battle' : 'Battle Details'}</h3>
            
            {editError && (
                <div className="mb-4 p-2 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-red-600 text-sm">{editError}</p>
                </div>
              )}

            {isEditing ? (
              // Edit Form
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Title</label>
                  <input
                    type="text"
                    name="title"
                    value={editFormData?.title || ''}
                    onChange={handleEditFormChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-iof focus:ring-iof"
                    disabled={editLoading}
                  />
                </div>
                 <div>
                  <label className="block text-sm font-medium text-gray-700">Description</label>
                  <input
                    type="text"
                    name="description"
                    value={editFormData?.description || ''}
                    onChange={handleEditFormChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-iof focus:ring-iof"
                    disabled={editLoading}
                  />
                </div>
                 <div>
                  <label className="block text-sm font-medium text-gray-700">Subject</label>
                  <select
                    name="subject"
                    value={editFormData?.subject || ''}
                    onChange={handleEditFormChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-iof focus:ring-iof"
                    disabled={editLoading}
                   >
                     <option value="">Select Subject</option>
                     {availableSubjects.map(subject => (
                       <option key={subject} value={subject}>{subject}</option>
                     ))}
                   </select>
                </div>
                 <div>
                  <label className="block text-sm font-medium text-gray-700">Difficulty</label>
                   <select
                    name="difficulty"
                    value={editFormData?.difficulty || ''}
                    onChange={handleEditFormChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-iof focus:ring-iof"
                    disabled={editLoading}
                  >
                    <option value="beginner">Beginner</option>
                    <option value="intermediate">Intermediate</option>
                    <option value="advanced">Advanced</option>
                  </select>
                </div>
                 <div>
                  <label className="block text-sm font-medium text-gray-700">Question Count</label>
                  <input
                    type="number"
                    name="questionCount"
                    min="1"
                    max="50"
                    value={editFormData?.questionCount || 0}
                    onChange={handleEditFormChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-iof focus:ring-iof"
                    disabled={editLoading}
                  />
                </div>
                 <div>
                  <label className="block text-sm font-medium text-gray-700">Time Limit (minutes)</label>
                  <input
                    type="number"
                    name="timeLimit"
                    min="5"
                    max="180"
                    value={editFormData?.timeLimit || 0}
                    onChange={handleEditFormChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-iof focus:ring-iof"
                    disabled={editLoading}
                  />
                </div>
                 <div>
                  <label className="block text-sm font-medium text-gray-700">End Date</label>
                  <input
                    type="datetime-local"
                    name="endDate"
                    value={editFormData?.endDate || ''}
                    onChange={handleEditFormChange}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-iof focus:ring-iof"
                    disabled={editLoading}
                  />
                </div>
                {/* Add more editable fields here as needed */}
                 <div className="flex justify-end space-x-4">
                    <button 
                       onClick={() => setIsEditing(false)}
                       className="px-4 py-2 bg-gray-200 rounded-md hover:bg-gray-300"
                       disabled={editLoading}
                      >
                        Cancel
                      </button>
                    <button 
                       onClick={updateBattle}
                       className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
                       disabled={editLoading}
                      >
                        {editLoading ? 'Saving...' : 'Save Changes'}
                      </button>
                 </div>
              </div>
            ) : (
              // View Details
              <div className="space-y-4">
                <div>
                  <p className="font-medium">Title:</p>
                  <p className="text-gray-700">{selectedBattle.title}</p>
                </div>
                 <div>
                  <p className="font-medium">Description:</p>
                  <p className="text-gray-700">{selectedBattle.description}</p>
                </div>
                 <div>
                  <p className="font-medium">Subject:</p>
                  <p className="text-gray-700">{selectedBattle.subject}</p>
                </div>
                 <div>
                  <p className="font-medium">Difficulty:</p>
                  <p className="text-gray-700">{selectedBattle.difficulty}</p>
                </div>
                 <div>
                  <p className="font-medium">Question Count:</p>
                  <p className="text-gray-700">{selectedBattle.questionCount}</p>
                </div>
                 <div>
                  <p className="font-medium">Time Limit (minutes):</p>
                  <p className="text-gray-700">{selectedBattle.timeLimit}</p>
                </div>
                 <div>
                  <p className="font-medium">End Date:</p>
                  <p className="text-gray-700">{selectedBattle.endDate?.toDate().toLocaleString()}</p>
                </div>
                 <div>
                  <p className="font-medium">Created At:</p>
                  <p className="text-gray-700">{selectedBattle.createdAt?.toDate().toLocaleString()}</p>
                </div>
                {/* Add more details here as needed */}
                <div className="flex justify-end space-x-4">
                   <button 
                     onClick={() => handleEditButtonClick(selectedBattle)}
                     className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                    >
                      Edit
                   </button>
                  <button 
                    onClick={() => setShowBattleDetailsModal(false)}
                    className="px-4 py-2 bg-gray-200 rounded-md hover:bg-gray-300"
                   >
                      Close
                   </button>
                 </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Question Selection Modal */}
      {showQuestionSelectionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg p-6 max-w-3xl w-full h-[80vh] flex flex-col">
            <h3 className="text-xl font-semibold mb-4">Select Questions for Battle</h3>

            {/* Filter and Search Options */}
            <div className="mb-4 bg-gray-100 p-3 rounded-md space-y-3">
               <input
                 type="text"
                 placeholder="Search questions..."
                 value={filterSearchTerm}
                 onChange={(e) => setFilterSearchTerm(e.target.value)}
                 className="w-full rounded-md border-gray-300 shadow-sm focus:border-iof focus:ring-iof text-sm"
               />
               <div className="grid grid-cols-3 gap-3">
                  {/* Subject Filter */}
                 <select
                   value={filterSubject}
                   onChange={(e) => {
                      setFilterSubject(e.target.value);
                      setFilterTopic('all'); // Reset topic when subject changes
                   }}
                   className="rounded-md border-gray-300 shadow-sm focus:border-iof focus:ring-iof text-sm"
                 >
                   <option value="all">All Subjects</option>
                   {availableSubjects.map(subject => (
                     <option key={subject} value={subject}>{subject}</option>
                   ))}
                 </select>

                 {/* Topic Filter (dependent on subject) */}
                 <select
                   value={filterTopic}
                   onChange={(e) => setFilterTopic(e.target.value)}
                   className="rounded-md border-gray-300 shadow-sm focus:border-iof focus:ring-iof text-sm"
                   disabled={filterSubject === 'all'}
                 >
                   <option value="all">All Topics</option>
                   {filterSubject !== 'all' && availableTopics.has(filterSubject) &&
                      availableTopics.get(filterSubject).map(topic => (
                         <option key={topic} value={topic}>{topic}</option>
                   ))}
                 </select>

                 {/* Difficulty Filter */}
                 <select
                    value={filterDifficulty}
                    onChange={(e) => setFilterDifficulty(e.target.value)}
                    className="rounded-md border-gray-300 shadow-sm focus:border-iof focus:ring-iof text-sm"
                  >
                    <option value="all">All Difficulties</option>
                    <option value="beginner">Beginner</option>
                    <option value="intermediate">Intermediate</option>
                    <option value="advanced">Advanced</option>
                 </select>
               </div>
            </div>

            {/* Question List */}
            <div className="flex-grow overflow-y-auto space-y-3 pr-2">
               {allQuestions.length === 0 ? (
                 <p className="text-gray-500">Loading questions or no questions available...</p>
               ) : (
                 // Use filtered questions here
                 getFilteredQuestions().map(question => (
                   <div 
                     key={question.id} 
                     className={`p-3 border rounded-md cursor-pointer ${selectedQuestionsForNewBattle.find(q => q.id === question.id) ? 'bg-blue-100 border-blue-400' : 'bg-gray-50 hover:bg-gray-100'}`}
                     onClick={() => handleQuestionSelect(question.id)}
                   >
                     <p className="font-semibold text-sm">{question.question}</p>
                     <p className="text-xs text-gray-600">Subject: {question.subject}, Topic: {question.topic}, Difficulty: {question.difficulty}</p>
                   </div>
                 ))
               )}
            </div>

            {/* Footer with selected count and action buttons */}
            <div className="mt-4 pt-4 border-t flex justify-between items-center">
               <div>
                 Selected: <span className="font-semibold">{selectedQuestionsForNewBattle.length}</span> questions
               </div>
               <div className="space-x-4">
                 <button
                   onClick={() => setSelectedQuestionsForNewBattle([])} // Clear selection
                   className="px-4 py-2 bg-gray-200 rounded-md hover:bg-gray-300"
                 >
                   Clear Selection
                 </button>
                 <button
                   onClick={closeQuestionSelectionModal}
                   className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                 >
                   Done
                 </button>
               </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

export default QuizBattleManagement; 